<template>
  <div class="container-fluid">
    <div class="row justify-content-center mb-4">
      <div class="col-lg-10 col-md" style="max-width: 1200px;">
        <h1 class="h1">The Altura Group Dashboard</h1>
        <hr />
        <div class="row">
          <div class="col">
            <h2 class="shifted">Total Currency Positions</h2>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <vue-good-table :columns="masterTable.columns" :rows="masterTable.rows"></vue-good-table>
          </div>
          <div class="col">
            <h3 class="shifted">NAV: {{ formatPrice(smoothedNav) }}</h3>
            <h3 class="shifted">PnL: {{ formatPrice(smoothedNav - previousSettleNav) }}</h3>
            <h3 class="shifted">Updated: {{ formatTimestamp(timestamp) }}</h3>
          </div>
        </div>
        <hr />
        <h2 class="shifted">Positions</h2>
        <vue-good-table :columns="positionsTable.columns" :rows="positionsTable.rows"></vue-good-table>
        <hr />
        <h2 class="shifted">Accounts</h2>
        <vue-good-table :columns="accountsTable.columns" :rows="accountsTable.rows"></vue-good-table>
      </div>
    </div>
  </div>
</template>

<script>
import "vue-good-table/dist/vue-good-table.css";
import { VueGoodTable } from "vue-good-table";

import _ from "lodash";
import numeral from "numeral";
import moment from "moment";
import API from "@aws-amplify/api";

const dollarizeSpotPrices = function(spot) {
  // Output dollarized exchange rates
  const spotPrices = {};
  for (let pair in spot) {
    if (pair == "BTC-USDT") spotPrices["USDT"] = spot["BTC-USD"] / spot[pair];
    else spotPrices[pair.split('-')[0]] = spot[pair];
  }

  return spotPrices
}

const calculateNav = function(totalCurrency) {
  let nav = 0;

  for (let currency in totalCurrency) {
    const price = totalCurrency[currency]["spot"];
    const coin = totalCurrency[currency]["balance"];
    const liability = totalCurrency[currency]["liability"];
    nav += price * (coin - liability);
  }

  return nav
}

const processTotalCurrency = function(portfolio, spotPrices) {
  const dollarizedSpotPrices = dollarizeSpotPrices(spotPrices);
  const totalCurrency = { USD: { balance: 0.0, liability: 0.0, spot: 1.0 } };

  for (let exchange in portfolio) {
    const exchangeData = portfolio[exchange];

    for (let account in exchangeData) {
      const accountData = exchangeData[account];

      for (let coin in accountData["equity"]) {
        const accountCurrency = accountData["equity"][coin]["currency"];

        if (!(accountCurrency in totalCurrency))
            totalCurrency[accountCurrency] = {
                balance: 0.0,
                liability: 0.0,
                spot: dollarizedSpotPrices[accountCurrency]
            }

        const currency_total = accountData['equity'][coin]['total']
        if (currency_total < 0)
          totalCurrency[accountCurrency]['liability'] -=
              accountData['equity'][coin]['total']
        else
          totalCurrency[accountCurrency]['balance'] +=
              accountData['equity'][coin]['total']
      }
    }
  }

  return totalCurrency;
};

const processAccounts = function(portfolio) {
  const accounts = [];

  for (let exchange in portfolio) {
    const exchangeData = portfolio[exchange];

    for (let account in exchangeData) {
      const accountData = exchangeData[account];

      let has_non_zero_equity = false;
      for (let coin in accountData["equity"]) {
        has_non_zero_equity |= (accountData["equity"][coin]["total"] != 0);
      }

      if (!has_non_zero_equity)
        continue;

      for (let coin in accountData["equity"]) {

        accounts.push({
          exchange: exchange,
          account: account,
          currency: accountData["equity"][coin]["currency"],
          balance: accountData["equity"][coin]["total"],
          timestamp: accountData["equity"][coin]["timestamp"]
        });
      }
    }
  }

  accounts.sort((a, b) => {
    if (a["exchange"] < b["exchange"]) return -1;
    else if (a["exchange"] > b["exchange"]) return 1;
    else {
      if (a["account"] < b["account"]) return -1;
      else if (a["account"] > b["account"]) return 1;
      else return 0;
    }
  });

  return accounts;
};

const processPositions = function(portfolio) {
  const positions = [];

  for (let exchange in portfolio) {
    const exchangeData = portfolio[exchange];

    for (let account in exchangeData) {
      const accountData = exchangeData[account];

      let has_non_zero_equity = false;
      for (let coin in accountData["equity"]) {
        has_non_zero_equity |= (accountData["equity"][coin]["total"] != 0);
      }

      if (!has_non_zero_equity)
        continue;

      for (let position in accountData["positions"]) {
        const positionData = accountData["positions"][position];

        const liquidationPrice = positionData["liquidationPrice"];
        const markPrice = positionData["markPrice"];

        let liquidationDistance;
        if (liquidationPrice === 0) liquidationDistance = 1.0;
        else
          liquidationDistance =
            Math.abs(liquidationPrice - markPrice) / markPrice;

        positions.push({
          exchange: exchange,
          account: account,
          symbol: position,
          quantity: positionData["quantity"],
          markPrice: markPrice,
          liquidationPrice: liquidationPrice,
          liquidationDistance: liquidationDistance,
          timestamp: positionData["timestamp"]
        });
      }
    }
  }

  positions.sort((a, b) => {
    if (a["exchange"] < b["exchange"]) return -1;
    else if (a["exchange"] > b["exchange"]) return 1;
    else {
      if (a["account"] < b["account"]) return -1;
      else if (a["account"] > b["account"]) return 1;
      else {
        if (a["symbol"] < b["symbol"]) return -1;
        else if (a["symbol"] > b["symbol"]) return 1;
        else return 0;
      }
    }
  });

  return positions;
};

const transformMasterRows = function(totalCurrency) {
  const masterRows = [];

  for (let key in totalCurrency) {
    masterRows.push({ ...{ currency: key }, ...totalCurrency[key] });
  }

  return transformRows(masterRows);
};

const transformRows = function(rows) {
  const cloned = _.cloneDeep(rows);

  const transformed = cloned.map((row, index) => {
    row["id"] = index;
    return row;
  });

  return transformed;
};

function median(arr) {
  const cloned = _.cloneDeep(arr);
  cloned.sort(function(a, b) {
    return a - b;
  });
  var i = cloned.length / 2;
  return i % 1 == 0 ? (cloned[i - 1] + cloned[i]) / 2 : cloned[Math.floor(i)];
}

let updateTimeout;
export default {
  name: "Dashboard",
  components: {
    VueGoodTable
  },
  data() {
    return {
      timestamp: 0,
      masterTable: {
        columns: [
          {
            label: "Currency",
            field: "currency"
          },
          {
            label: "Balance",
            field: "balance",
            formatFn: val => this.formatBalance(val, 4)
          },
          {
            label: "Liability",
            field: "liability",
            formatFn: val => this.formatBalance(val, 4)
          },
          {
            label: "Mark Price",
            field: "spot",
            formatFn: val => this.formatBalance(val, 4)
          }
        ],
        rows: []
      },
      accountsTable: {
        columns: [
          {
            label: "Exchange",
            field: "exchange"
          },
          {
            label: "Account",
            field: "account"
          },
          {
            label: "Currency",
            field: "currency"
          },
          {
            label: "Balance",
            field: "balance",
            formatFn: val => this.formatBalance(val, 4)
          },
          {
            label: "Updated",
            field: "timestamp",
            formatFn: this.formatSince
          }
        ],
        rows: []
      },
      positionsTable: {
        columns: [
          {
            label: "Exchange",
            field: "exchange"
          },
          {
            label: "Account",
            field: "account"
          },
          {
            label: "Symbol",
            field: "symbol"
          },
          {
            label: "Quantity",
            field: "quantity",
            formatFn: val => this.formatQuantity(val, 4)
          },
          {
            label: "Mark",
            field: "markPrice",
            type: "number"
          },
          {
            label: "Liq",
            field: "liquidationPrice",
            type: "number"
          },
          {
            label: "Liq %",
            field: "liquidationDistance",
            type: "percentage"
          },
          {
            label: "Updated",
            field: "timestamp",
            formatFn: this.formatSince
          }
        ],
        rows: []
      },
      nav: [],
      previousSettleNav: 0.0,
      smoothedNav: 0.0,
      totalCurrency: {},
      accounts: [],
      positions: []
    };
  },
  methods: {
    update: function() {
      this.timestamp = Date.now();

        API.get("api23b65950", "/dashboard", { response: true })
          .then(res => {
            this.processDashboard(res["data"]["output"]["data"]);
            return API.get("api23b65950", "/dashboard/settle", { response: true })
          })
          .then(res => {
            this.processPreviousSettle(res["data"]["output"]["data"]);
          })
          .catch(() => {})
          .finally(() => {
            updateTimeout = setTimeout(this.update, 2000);
          });
    },
    processPreviousSettle: function(data)
    {
      const totalCurrency = processTotalCurrency(
        data["portfolio"],
        data["spot"]
      );

      const previousSettleNav = calculateNav(totalCurrency)

      this.previousSettleNav = previousSettleNav
    },
    processDashboard: function(data) {
      this.totalCurrency = processTotalCurrency(
        data["portfolio"],
        data["spot"]
      );

      this.smoothedNav = this.processNav(this.totalCurrency);

      this.accounts = processAccounts(data["portfolio"]);
      this.positions = processPositions(data["portfolio"]);

      this.masterTable.rows = transformMasterRows(this.totalCurrency);
      this.accountsTable.rows = transformRows(this.accounts);
      this.positionsTable.rows = transformRows(this.positions);
    },
    processNav: function(totalCurrency) {
      const currentNav = calculateNav(totalCurrency)

      this.nav.push(currentNav);

      if (this.nav.length >= 30) this.nav.shift();

      return median(this.nav);
    },
    formatPrice(value, digits) {
      const val = (value / 1).toFixed(digits);
      return "$" + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatQuantity(value) {
      let quantity_string = numeral(value).format('0,0.0000')
      let last_char = quantity_string.charAt(quantity_string.length - 1)

      while (last_char === '0') {
        quantity_string = quantity_string.substr(0, quantity_string.length - 1)
        last_char = quantity_string.charAt(quantity_string.length - 1)
      }

      if (last_char === '.')
          quantity_string = quantity_string.substr(0, quantity_string.length - 1)

      return quantity_string
    },
    formatBalance(value, digits) {
      return value.toFixed(digits);
    },
    formatTimestamp(val) {
      return moment(val).format("MMM D HH:mm:ss");
    },
    formatSince(val) {
      const since = moment
        .duration(moment(this.timestamp).diff(moment(val)))
        .asSeconds()
        .toFixed(0);
      if (since <= 60) return since.toString() + " seconds ago";
      return "> 60 seconds ago";
    }
  },
  created: function() {
    this.update();
  },
  destroyed: function() {
    clearTimeout(updateTimeout);
  }
};
</script>
